import { getFunctions, httpsCallable } from 'firebase/functions';
import type { ProjectPLBucket, ProjectPhase, TransactionSplit } from '../types';

/** Per-request batch size — keep ≤ deployed `BULK_CATEGORIZE_MAX` in Cloud Functions. */
export const BULK_CATEGORIZE_CHUNK_SIZE = 100;

function callableErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message).trim();
    if (message) return message;
  }
  return fallback;
}

export async function backfillCardExpenseAssignments(): Promise<{
  updated: number;
  skipped: number;
  cardCount: number;
  sequenceWritten?: number;
  sequenceMatched?: number;
  sequenceAssigned?: number;
  sequenceError?: string | null;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'backfillCardExpenseAssignments');
  const result = await fn({});
  return result.data as {
    updated: number;
    skipped: number;
    cardCount: number;
    sequenceWritten?: number;
    sequenceMatched?: number;
    sequenceAssigned?: number;
    sequenceError?: string | null;
  };
}

export async function propagateCardAssignments(params?: {
  changedCardIds?: string[];
}): Promise<{ linked: number; assigned: number; skipped: number }> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'propagateCardAssignments');
  const result = await fn(params || {});
  return result.data as { linked: number; assigned: number; skipped: number };
}

export async function categorizeCardExpense(params: {
  expenseId: string;
  target?: string | null;
  dealTitle?: string;
  excludeReason?: string;
  splits?: TransactionSplit[];
  legendId?: string | null;
  plBucket?: ProjectPLBucket | null;
  plBucketSource?: 'auto' | 'user' | 'pm_override';
  linkTargetId?: string | null;
  projectPhase?: ProjectPhase;
  qbAccountId?: string | null;
  qbAccountName?: string | null;
}): Promise<void> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'categorizeCardExpense');
  try {
    await fn(params);
  } catch (err) {
    throw new Error(callableErrorMessage(err, 'Could not save categorization.'));
  }
}

export async function syncCardExpenseToQuickBooks(params: {
  expenseId: string;
}): Promise<{
  expenseId: string;
  linkedExpenseId: string;
  qbWriteBackStatus?: string;
  qbWriteBackError?: string;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'syncCardExpenseToQuickBooks');
  try {
    const result = await fn(params);
    return result.data as {
      expenseId: string;
      linkedExpenseId: string;
      qbWriteBackStatus?: string;
      qbWriteBackError?: string;
    };
  } catch (err) {
    throw new Error(callableErrorMessage(err, 'QuickBooks sync failed.'));
  }
}

export async function categorizeUnifiedExpense(params: {
  expenseId: string;
  target?: string | null;
  dealTitle?: string;
  excludeReason?: string;
  splits?: TransactionSplit[];
  legendId?: string | null;
  plBucket?: ProjectPLBucket | null;
  plBucketSource?: 'auto' | 'user' | 'pm_override';
  linkTargetId?: string | null;
  projectPhase?: ProjectPhase;
  qbAccountId?: string | null;
  qbAccountName?: string | null;
}): Promise<void> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'categorizeUnifiedExpense');
  try {
    await fn(params);
  } catch (err) {
    throw new Error(callableErrorMessage(err, 'Could not save categorization.'));
  }
}

export async function categorizeCardExpensesBulk(params: {
  expenseIds: string[];
  target: string | null;
  dealTitle?: string;
  excludeReason?: string;
  legendId?: string | null;
  plBucket?: ProjectPLBucket | null;
  linkTargetId?: string | null;
  projectPhase?: ProjectPhase;
  qbAccountId?: string | null;
  qbAccountName?: string | null;
  onProgress?: (processed: number, total: number) => void;
}): Promise<{ updated: number; skipped: number; denied: number; notFound: number }> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'categorizeCardExpensesBulk');
  const ids = Array.from(new Set(params.expenseIds.map(id => id.trim()).filter(Boolean)));
  const totals = { updated: 0, skipped: 0, denied: 0, notFound: 0 };

  try {
    for (let i = 0; i < ids.length; i += BULK_CATEGORIZE_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + BULK_CATEGORIZE_CHUNK_SIZE);
      const result = await fn({
        expenseIds: chunk,
        target: params.target,
        dealTitle: params.dealTitle,
        excludeReason: params.excludeReason,
        legendId: params.legendId,
        plBucket: params.plBucket,
        linkTargetId: params.linkTargetId,
        projectPhase: params.projectPhase,
        qbAccountId: params.qbAccountId,
        qbAccountName: params.qbAccountName,
      });
      const data = result.data as {
        updated: number;
        skipped: number;
        denied: number;
        notFound: number;
      };
      totals.updated += data.updated;
      totals.skipped += data.skipped;
      totals.denied += data.denied;
      totals.notFound += data.notFound;
      params.onProgress?.(Math.min(i + chunk.length, ids.length), ids.length);
    }
    return totals;
  } catch (err) {
    throw new Error(callableErrorMessage(err, 'Bulk update failed.'));
  }
}

export const CARD_EXPENSE_OVERHEAD = '__overhead__';
export const CARD_EXPENSE_EXCLUDE = '__exclude__';
/** Picker sentinel for exclude — maps to {@link CARD_EXPENSE_EXCLUDE} on submit. */
export const QB_EXPENSE_EXCLUDE_ASSIGN = '__exclude_assign__';
